import {useEffect, useState, useRef} from 'react';
import {Subscription, filter} from 'rxjs';
import {Conversation} from '@stripe-internal/conversations-client';
import {BlinkingCursor} from './BlinkingCursor';

const NON_TEXT_LOADER_DELAY = 500;

export function LoadingIndicator({conversation}: {conversation: Conversation}) {
  const [loading, setLoading] = useState<boolean>(false);
  const latestTimeout = useRef<null | ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    conversation
      .eventsObservable()
      .pipe(filter((e) => e.name === 'conversation-message-added'))
      .subscribe(() => {
        if (latestTimeout.current) {
          clearTimeout(latestTimeout.current);
        }

        setLoading(false);
      });
  }, [conversation]);

  useEffect(() => {
    // We need to indicate loading if this is the "placeholder"
    // conversation which exists while a new conversation is being
    // created. This shows the cursor immediately when the app is
    // opened
    setLoading(conversation.context.placeholderConversation as boolean);

    const sub = new Subscription();
    sub.add(
      conversation.client
        .conversationEventsObservable()
        .pipe(
          filter(
            (e) =>
              e.name === 'conversation-typing-started' &&
              conversation.getParticipant(e.participantId)?.role === 'bot',
          ),
        )
        .subscribe(() => {
          if (latestTimeout.current) {
            clearTimeout(latestTimeout.current);
          }

          latestTimeout.current = setTimeout(() => {
            setLoading(true);
          }, NON_TEXT_LOADER_DELAY);
        }),
    );
    return () => {
      sub.unsubscribe();
    };
  }, [conversation]);

  return loading ? <BlinkingCursor /> : null;
}
